import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { SaveButton, useNotify, useRedirect, useRefresh } from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { ConditionalConfirm } from "../../../components/common/ConditionalConfirm";
import { buildAppointmentNote } from "../../../components/common/scheduler/scheduleHistory";
import { logError } from "../../../utils/appUtils";
import { VALIDATION_MSG } from "../../../utils/constants";
import { isUserFacingError } from "../../../utils/lambdaErrors";
import {
  areTimeslotsEqual,
  validateAppointment,
} from "../../../utils/dateUtils";
import { deepRedirect, historyAutoGen } from "../../../utils/utils";

/**
 * Carrier dispatch save: the only editable field on the form is
 * scheduledDateTime, so this books/re-books the appointment and writes the
 * shipment header. Routes through the lambda writer (data.__saveContext) so
 * the Timeslot and the shipment's schedule link move in one transaction; the
 * dataProvider sanitizes on return.
 */
export const DispatchSaveButton = ({
  record,
  backPath,
  selectedTimeslot,
  isSaving,
  setIsSaving,
  ...props
}) => {
  const form = useForm();
  const history = useHistory();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const { pristine, submitting } = useFormState({
    subscription: { pristine: true, submitting: true },
  });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  const formValidate = () => {
    if (form.getState().invalid) notify(VALIDATION_MSG, "warning");
  };

  const handleValidateSave = async () => {
    // Re-check availability only when the carrier picked a different slot.
    if (
      selectedTimeslot &&
      !areTimeslotsEqual(record.timeslot, selectedTimeslot)
    ) {
      let unavailable;
      try {
        unavailable = await validateAppointment(
          selectedTimeslot,
          record.timeslot?.id,
        );
      } catch (error) {
        // The availability read failed, so we cannot tell whether the slot is
        // still free — block rather than book blind. Caught here and not left
        // to reject: react-admin calls handleSubmitWithRedirect without
        // awaiting it, so an escaping rejection makes the click do nothing at
        // all, with no message and the button still enabled.
        logError(
          "carrier:inbounds:DispatchSaveButton:validateAppointment",
          error,
          notify,
          redirect,
        );
        return;
      }
      if (unavailable) {
        setMessage(
          `${selectedTimeslot.time} is no longer available. Please select a new time.`,
        );
        setOpen(true);
        return;
      }
    }
    handleSave();
  };

  const handleDialogClose = () => {
    setOpen(false);
    // This dialog only opens because the server and the form disagree — a slot
    // that went, a shipment that moved out from under the booking. Dismissing it
    // should leave the carrier looking at what is actually stored rather than at
    // the pick that was just refused: react-admin keys the form on the version
    // this bumps, so the fields reinitialize from the refetched record, and
    // useScheduleEventType drops the pick on the same bump so the two cannot
    // disagree afterwards.
    refresh();
  };

  const handleSave = () => {
    formValidate();
    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data };

    // Fall back to the existing booking when the scheduler was never opened.
    data.timeslot =
      selectedTimeslot === undefined ? record.timeslot : selectedTimeslot;

    // Appends any other field's change to data.history in place (no-op when the
    // form is unchanged — assigning the return value would clear it).
    // scheduledDateTime is excluded because the generic pass cannot describe a
    // Date; buildAppointmentNote covers it from the timeslots instead, so the
    // first booking is recorded and a reschedule is not logged twice.
    const historyBefore = data.history?.length ?? 0;
    historyAutoGen(form, data, ["scheduledDateTime"]);
    const appointmentNote = buildAppointmentNote(
      record.timeslot,
      data.timeslot,
    );
    if (appointmentNote)
      data.history = [...(data.history ?? []), appointmentNote];
    // Both passes append, so the tail is exactly what this save added. The
    // lambda appends that list to the stored history instead of replacing it,
    // which keeps a note the yard wrote while this form was open.
    const historyAppendList = (data.history ?? []).slice(historyBefore);
    // AppSync auto-stamps updatedAt; the direct lambda write does not.
    data.updatedAt = new Date().toISOString();
    // Carry save context to the dataProvider's inboundShipments branch;
    // stripped there before the lambda call. Its presence is also the opt-in
    // gate — RA updates without it pass through to AppSync. scheduleOnly picks
    // the narrow appointment transaction: this form edits scheduledDateTime and
    // nothing else, so the save must not re-send the tally sheet and inventory
    // it merely displays.
    data.__saveContext = {
      isRollback: false,
      scheduleOnly: true,
      historyAppendList,
    };
    return data;
  };

  const onSuccess = () => {
    notify("Delivery scheduled successfully", "success");
    deepRedirect(history, backPath);
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    // The lambda flags the errors it wrote for the carrier — today that is the
    // shipment having moved out from under the form. Those name the problem and
    // say what to do about it, so they belong in the same dialog the
    // availability check uses. Behind logError's generic notice they told the
    // carrier nothing, which left refreshing and picking again as something they
    // had to guess at.
    if (isUserFacingError(error)) {
      setMessage(error.message);
      setOpen(true);
      // Still recorded, just without the second, contradictory toast: losing
      // this race is expected under contention, and how often it happens is
      // worth being able to see.
      logError("carrier:inbounds:DispatchSaveButton", error, false, false);
      return;
    }
    logError("carrier:inbounds:DispatchSaveButton", error, notify, redirect);
  };

  return (
    <>
      <SaveButton
        {...props}
        label="Save"
        submitOnEnter={false}
        undoable={false}
        disabled={pristine || isSaving}
        saving={isSaving || submitting}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <ConditionalConfirm
        isOpen={open}
        loading={isSaving}
        title="Save Confirmation"
        content={message}
        cancel="Ok"
        onClose={handleDialogClose}
      />
    </>
  );
};
